import React, { useEffect, useState } from 'react'
import { connect } from "react-redux";
import { adminHttp } from "~/Admin/services/http";
import { produce } from "immer";
import ConfirmedButton from "~/Components/ConfirmedButton/ConfirmedButton";
import ValidationError from "~/Components/Forms/ValidationError";
import { CloseIcon, EditIcon, ArrowBackIcon } from '~/Components/Icons/Icons';
import { Form } from "react-bootstrap";
import { toggleArray } from "~/services/helpers";
import classnames from "classnames";
import Translated from "~/Admin/AdminMultilang/Translated";
import { toast } from 'react-toastify';

const AdminPackageManagement = ({ match, event, ...props }) => {

    const [response, setResponse] = useState([]);
    const [selected, setSelected] = useState(null);

    const [form, setForm] = useState({
        name: '',
        hospitality: false,
        experiences: [],
        hospitalities: [],
        steps: {},
        translations: {},
        display_order: ''
    });

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeExp, setActiveExp] = useState(null);

    const loadData = () => {
        adminHttp
            .get('/events/' + event.id + '/packages',)
            .then(res => {
                setResponse(res.data.sort((a, b) => a.display_order - b.display_order));
                setSelected(res.data[0]);
            });
    };

    const handleAddPackage = () => {
        toast.promise(
            adminHttp.post('/events/' + event.id + '/packages'),
            {
                pending: 'Adding package...',
                success: {
                    render(res) {
                        setResponse(res.data?.data);
                        return 'New package added'
                    }
                },
                error: 'Something went wrong!'
            }
        )
    };

    const handleDeletePackage = () => {
        toast.promise(
            adminHttp.delete('/events/' + event.id + '/packages/' + selected.id),
            {
                pending: 'Deleting package...',
                success: {
                    render() {
                        loadData();
                        return 'Package deleted'
                    }
                },
                error: 'Something went wrong!'
            }
        )
    };

    useEffect(loadData, [event]);

    useEffect(() => {

        setForm(produce(form, (draft) => {
            if (selected) {
                draft.name = selected.name;
                draft.hospitality = selected.hospitality;
                draft.hospitalities = [];
                draft.display_order = selected.display_order;
                draft.translations = selected.translations;
                draft.experiences = selected.experiences || [];
                draft.steps = selected.steps.values || {};

                if (selected.hospitalities) {
                    draft.hospitalities = selected.hospitalities.map(h => h.id);
                }
            }
        }));

        setSuccess(null);
        setError(null);
        setActiveExp(null);

    }, [selected]);

    const handleChange = (e) => {
        const name = e.target.name;
        const value = e.target.value;
        setForm({ ...form, [name]: value });
    };

    const handleExpChange = (e, key) => {
        const value = e.target.value;
        const f = produce(form, (draft) => {
            draft.experiences[key].name = value;
        });
        setForm(f);
    };

    const handleExpTranslationChange = (values, key) => {
        const f = produce(form, (draft) => {
            draft.experiences[key].translations = values;
        });
        setForm(f);
    };

    const handleHospitalityChange = () => {
        setForm({
            ...form,
            hospitality: !form.hospitality
        });
    };

    const handleStepChange = (step) => {
        const f = produce(form, (draft) => {
            draft.steps[step] = !draft.steps[step];
        });
        setForm(f);
    };

    const handleHospitalitiesChange = (event) => {
        setForm({
            ...form,
            hospitalities: toggleArray(form.hospitalities, parseInt(event.target.value))
        });
    }

    const handleExpAdd = () => {
        const f = produce(form, (draft) => {
            draft.experiences.push({
                name: 'New Experience'
            });
        });

        setForm(f);
        setActiveExp(f.experiences.length - 1);
    };

    const handleDeleteExp = (index) => {

        const f = produce(form, (draft) => {
            draft.experiences.splice(index, 1);
        });

        setForm(f);
    }

    const handleSave = () => {
        toast.promise(
            adminHttp.put('/events/' + event.id + '/packages/' + selected.id, form),
            {
                pending: 'Saving Package...',
                success: {
                    render(res) {
                        setError(null);
                        setSuccess('Package has been saved!');
                        setActiveExp(null);

                        let indx = response.findIndex(row => row.id === selected.id);

                        setResponse(produce(response, (draft) => {
                            draft[indx] = res.data?.data;
                        }));
                        loadData();
                        return 'Package saved'
                    }
                },
                error: {
                    render(error) {
                        setSuccess(null);
                        setError(error.data?.response?.data);
                        return 'Something went wrong!'
                    }
                }
            }
        )
    };

    const stepMap = {
        special_requirements: 'Special Requirements',
        hotel_rooms: 'Hotel Rooms',
        travel: 'Flight & Travel',
        emergency_contact: 'Emergency Contact',
        documents: 'Documents',
    };

    return (

        <div className="admin-inventory">
            <div className="row">
                <div className="col-8">
                    <h2>Package Management</h2>
                </div>
                <div className="col-4 text-right">
                    <ConfirmedButton onClick={handleAddPackage} className="btn btn-primary btn-sm">
                        + New Package
                    </ConfirmedButton>
                </div>
            </div>
            <div className="row mt-4">
                <div className="col-3">
                    <div className="list-group">
                        {response.map(item =>
                            <a key={item.id} onClick={(e) => setSelected(item)}
                                className={classnames('list-group-item list-group-item-action cursor-pointer', { active: selected?.id === item.id })}>
                                {item.name}
                            </a>
                        )}
                    </div>
                </div>
                {selected && (
                    <div className="col-9">
                        <h3>Edit Package</h3>

                        {success && (
                            <div className="alert alert-success" role="alert">
                                {success}
                            </div>
                        )}

                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error.message || "Error saving package!"}
                            </div>
                        )}

                        <div className="form-group required">
                            <label>Package Name</label>

                            <Translated translations={form.translations}
                                onChanged={(values) => setForm({ ...form, translations: values })}>
                                <input
                                    onChange={handleChange}
                                    value={form.name}
                                    name="name"
                                    type="text"
                                    className="form-control" />
                            </Translated>

                            <ValidationError errors={error?.errors} name="name" />

                        </div>
                        <div className="form-group required">
                            <label>Experience List</label>

                            <div>
                                <ul className="ticked">
                                    {form.experiences?.map((e, key) => (
                                        <li key={key}>

                                            {activeExp === key && (
                                                <div className="row">
                                                    <div className="col-3">
                                                        <Translated enabled={!!e.id} translations={e.translations}
                                                            onChanged={(values) => handleExpTranslationChange(values, key)}>
                                                            <input name="name" value={e.name} onChange={e => handleExpChange(e, key)} />
                                                        </Translated>
                                                    </div>
                                                    <div className="col-2">
                                                        <a className="text-primary ml-2" href="#" onClick={() => setActiveExp(null)}>Done</a>
                                                    </div>
                                                </div>
                                            )}

                                            {activeExp !== key && (
                                                <>
                                                    <span>{e.name}</span>
                                                    <EditIcon onClick={() => setActiveExp(key)} classes="d-inline ml-2 cursor-pointer" />
                                                    <CloseIcon onClick={() => handleDeleteExp(key)} color="#999BB5" size={13} classes="d-inline ml-2 cursor-pointer" />
                                                </>
                                            )}

                                        </li>
                                    ))}
                                </ul>

                                <ValidationError errors={error?.errors} name="experiences" />
                            </div>

                            <a className="text-primary d-block" href="#" onClick={handleExpAdd}>+ New list item</a>
                        </div>
                        <hr />
                        <div className="form-group">
                            <label className="d-block">Hospitality</label>
                            <small className="d-block">Enable the toggle if this package includes hospitality.</small>

                            <Form.Check
                                type="checkbox"
                                onChange={handleHospitalityChange}
                                checked={form.hospitality}
                                label={form.hospitality ? 'Activated' : 'Deactivated'}
                            />

                        </div>

                        {form.hospitality && (
                            <div className="form-group">
                                {event.hospitalities.map((hosp, key) => {
                                    return (
                                        <Form.Check
                                            key={hosp.id}
                                            label={hosp.name}
                                            type="checkbox"
                                            id={'hosp-' + hosp.id}
                                            value={hosp.id}
                                            checked={form.hospitalities.indexOf(hosp.id) >= 0}
                                            onChange={(e) => handleHospitalitiesChange(e)}
                                        />
                                    );
                                })}

                                <ValidationError errors={error?.errors} name="hospitalities" />
                            </div>
                        )}

                        <div className="form-group">

                            <label className="d-block">Active steps</label>


                            {Object.keys(selected.steps.values).map((step) => {
                                return (
                                    <Form.Check
                                        key={step}
                                        label={stepMap[step]}
                                        type="checkbox"
                                        id={'step-' + step}
                                        value={step}
                                        checked={form.steps[step]}
                                        onChange={(e) => handleStepChange(step)}
                                    />
                                );
                            })}

                            <ValidationError errors={error?.errors} name="hospitalities" />
                        </div>

                        <div className="form-group">
                            <label>Display Order</label>

                            <input
                                onChange={handleChange}
                                value={form.display_order || ''}
                                name="display_order"
                                type="text"
                                className="form-control" />

                            <ValidationError errors={error?.errors} name="display_order" />

                        </div>

                        <div className="form-group">
                            <button
                                onClick={handleSave}
                                className="btn btn-primary btn-sm float-right">
                                Save Package
                            </button>

                            <ConfirmedButton
                                confirmText="Are you sure you want to delete ?"
                                onClick={handleDeletePackage}
                                className="btn btn-secondary btn-sm mr-3 float-right">
                                Delete Package
                            </ConfirmedButton>
                        </div>
                    </div>)}
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    user: state.auth?.user,
    event: state.event.current,
});

export default connect(mapStateToProps)(AdminPackageManagement);
